import { getPlatformDomain, parseCustomDomainFromHost, parseSubdomainFromHost } from './storefrontHost.js';

const STORAGE_KEY = 'aaa_storefront_context_retail';

let activeStoreId = null;
let activeStoreContext = null;

export function getApiBaseUrl(env = process.env) {
    return (
        env.REACT_APP_API_BASE_URL ||
        env.NEXT_PUBLIC_API_BASE_URL ||
        'http://localhost:9090'
    ).replace(/\/$/, '');
}

export function getExpectedStoreType(env = process.env) {
    return env.REACT_APP_EXPECTED_STORE_TYPE || env.NEXT_PUBLIC_EXPECTED_STORE_TYPE || null;
}

function storeTypeToPeerEnvSuffixes(storeType) {
    const brandByType = {
        restaurant: ['elvix'],
        retail: ['zilvix'],
        realEstate: ['sylvixr'],
        automotive: ['rovtrix'],
    };
    const raw = String(storeType || '').trim();
    const fromMap = brandByType[raw] || brandByType[raw.toLowerCase()];
    if (fromMap?.length) return fromMap.map((b) => String(b).toUpperCase());
    const fallback = String(raw)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .toUpperCase();
    return fallback ? [fallback] : [];
}

/** Link to the correct brand storefront when this app receives a type mismatch (409). */
export function buildPeerStorefrontHref(
    { actualType, orgSlug, storeSlug, subdomain },
    env = process.env,
) {
    const suffixes = storeTypeToPeerEnvSuffixes(actualType);
    let base = '';
    for (const suffix of suffixes) {
        base = String(
            env[`REACT_APP_PEER_STOREFRONT_URL_${suffix}`] ||
                env[`NEXT_PUBLIC_PEER_STOREFRONT_URL_${suffix}`] ||
                '',
        )
            .trim()
            .replace(/\/$/, '');
        if (base) break;
    }
    if (!base) return '';

    const sub = String(subdomain || '').trim().toLowerCase();
    if (sub) {
        try {
            const peer = new URL(base);
            return `${peer.protocol}//${sub}.${peer.hostname}`;
        } catch {
            /* fall through to path URL */
        }
    }

    if (orgSlug && storeSlug) {
        return `${base}/s/${encodeURIComponent(orgSlug)}/${encodeURIComponent(storeSlug)}`;
    }
    return base;
}

function throwStoreResolveError(data = {}, status = 0) {
    const err = new Error(data.message || 'Failed to resolve store');
    if (status === 409 || data.actualType) {
        err.code = 'STORE_TYPE_MISMATCH';
        err.expectedType = data.expectedType;
        err.actualType = data.actualType;
        err.storefrontApp = data.storefrontApp;
        err.orgSlug = data.orgSlug;
        err.storeSlug = data.storeSlug;
        err.subdomain = data.subdomain;
        err.peerHref = buildPeerStorefrontHref({
            actualType: data.actualType,
            orgSlug: data.orgSlug,
            storeSlug: data.storeSlug,
            subdomain: data.subdomain,
        });
    }
    throw err;
}

export function parseSlugFromPath(pathname = '') {
    const match = String(pathname).match(/^\/s\/([^/]+)\/([^/]+)(?:\/|$)/i);
    if (!match) return null;
    return { orgSlug: decodeURIComponent(match[1]), storeSlug: decodeURIComponent(match[2]) };
}

function isValidMongoStoreId(value) {
    return /^[a-f0-9]{24}$/i.test(String(value || '').trim());
}

function readEnvStoreId(env) {
    const raw = String(env.REACT_APP_STORE_ID || env.NEXT_PUBLIC_STORE_ID || '').trim();
    return isValidMongoStoreId(raw) ? raw : null;
}

function readEnvSlug(env, reactKey, nextKey) {
    const raw = String(env[reactKey] || env[nextKey] || '').trim();
    return raw || null;
}

export function readCachedStoreContext(expectedType = getExpectedStoreType()) {
    if (typeof window === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed?.store?.id) return null;
        if (expectedType && parsed.store.type && parsed.store.type !== expectedType) {
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function writeCachedStoreContext(context) {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
}

export function getActiveStoreId() {
    return activeStoreId;
}

export function getActiveStoreContext() {
    return activeStoreContext;
}

export function applyStoreContext(context) {
    activeStoreContext = context;
    activeStoreId = context?.store?.id || null;
    writeCachedStoreContext(context);
}

function buildRedirectPath(pathname, fromPrefix, toPrefix) {
    if (!pathname.includes(fromPrefix)) return toPrefix;
    return pathname.replace(fromPrefix, toPrefix);
}

async function resolveStore(apiBaseUrl, params) {
    let response;
    try {
        response = await fetch(`${apiBaseUrl}/api/public/store/resolve?${params.toString()}`);
    } catch (err) {
        const hint =
            apiBaseUrl.includes('vercel.app') || apiBaseUrl.includes('localhost')
                ? ` Check that the API is running at ${apiBaseUrl} and restart the storefront dev server after .env changes.`
                : '';
        throw new Error(
            `Could not reach the API at ${apiBaseUrl}.${hint} (${err.message || 'network error'})`,
        );
    }
    const data = await response.json().catch(() => ({}));
    return { response, data };
}

function buildEnvResolveParams(env, expectedType) {
    const directStoreId = readEnvStoreId(env);
    const envOrgSlug = readEnvSlug(env, 'REACT_APP_ORG_SLUG', 'NEXT_PUBLIC_ORG_SLUG');
    const envStoreSlug = readEnvSlug(env, 'REACT_APP_STORE_SLUG', 'NEXT_PUBLIC_STORE_SLUG');

    const params = new URLSearchParams();
    if (directStoreId) {
        params.set('storeId', directStoreId);
    } else if (envOrgSlug && envStoreSlug) {
        params.set('orgSlug', envOrgSlug);
        params.set('storeSlug', envStoreSlug);
    } else {
        return null;
    }

    if (expectedType) {
        params.set('expectedType', expectedType);
    }

    return { params, envOrgSlug, envStoreSlug };
}

export async function bootstrapStorefront({
    env = process.env,
    pathname = typeof window !== 'undefined' ? window.location.pathname : '',
} = {}) {
    const apiBaseUrl = getApiBaseUrl(env);
    const expectedType = getExpectedStoreType(env);
    const pathSlugs = parseSlugFromPath(pathname);
    const envResolve = buildEnvResolveParams(env, expectedType);
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const subdomainFromHost = parseSubdomainFromHost(hostname, getPlatformDomain(env));
    const customDomainFromHost = parseCustomDomainFromHost(hostname, getPlatformDomain(env));

    // URL path is explicit, but may point at another store type (e.g. properties URL on restaurant app).
    if (pathSlugs) {
        const pathParams = new URLSearchParams({
            orgSlug: pathSlugs.orgSlug,
            storeSlug: pathSlugs.storeSlug,
        });
        if (expectedType) {
            pathParams.set('expectedType', expectedType);
        }

        const { response: pathResponse, data: pathData } = await resolveStore(apiBaseUrl, pathParams);

        if (pathResponse.ok) {
            const matchesExpected = !expectedType || pathData.store?.type === expectedType;

            if (matchesExpected) {
                applyStoreContext({ ...pathData, routingMode: 'path' });
                return pathData;
            }

            if (envResolve) {
                const { response: envResponse, data: envData } = await resolveStore(
                    apiBaseUrl,
                    envResolve.params,
                );

                if (envResponse.ok) {
                    const fromPrefix = `/s/${pathSlugs.orgSlug}/${pathSlugs.storeSlug}`;
                    const toPrefix = `/s/${envData.organization.slug}/${envData.store.slug}`;
                    const err = new Error(
                        `Wrong storefront URL for this app. Use ${toPrefix} instead of ${fromPrefix}.`,
                    );
                    err.code = 'STORE_TYPE_MISMATCH_REDIRECT';
                    err.redirectPath = buildRedirectPath(pathname, fromPrefix, toPrefix);
                    throw err;
                }
            }

            throwStoreResolveError(
                {
                    message:
                        pathData.message ||
                        `Store type mismatch: expected ${expectedType}, got ${pathData.store?.type}`,
                    expectedType,
                    actualType: pathData.store?.type || pathData.actualType,
                    storefrontApp: pathData.storefrontApp,
                    orgSlug: pathData.organization?.slug || pathSlugs.orgSlug,
                    storeSlug: pathData.store?.slug || pathSlugs.storeSlug,
                },
                409,
            );
        }

        if (pathResponse.status === 409 || pathData.actualType) {
            throwStoreResolveError(
                {
                    ...pathData,
                    orgSlug: pathData.orgSlug || pathSlugs.orgSlug,
                    storeSlug: pathData.storeSlug || pathSlugs.storeSlug,
                },
                pathResponse.status,
            );
        }
    }

    // Prefer subdomain over custom-domain when both could apply.
    if (subdomainFromHost) {
        const subdomainParams = new URLSearchParams({ subdomain: subdomainFromHost });
        if (expectedType) {
            subdomainParams.set('expectedType', expectedType);
        }

        const { response, data } = await resolveStore(apiBaseUrl, subdomainParams);

        if (response.ok) {
            applyStoreContext({
                ...data,
                routingMode: 'subdomain',
                subdomain: subdomainFromHost,
            });
            return data;
        }

        throwStoreResolveError(data, response.status);
    }

    if (customDomainFromHost) {
        const customParams = new URLSearchParams({ customDomain: customDomainFromHost });
        if (expectedType) {
            customParams.set('expectedType', expectedType);
        }

        const { response, data } = await resolveStore(apiBaseUrl, customParams);

        if (response.ok) {
            applyStoreContext({
                ...data,
                routingMode: 'custom_domain',
                customDomain: customDomainFromHost,
            });
            return data;
        }

        throwStoreResolveError(data, response.status);
    }

    if (envResolve) {
        const { response, data } = await resolveStore(apiBaseUrl, envResolve.params);

        if (response.ok) {
            applyStoreContext({ ...data, routingMode: 'path' });
            return data;
        }

        throwStoreResolveError(data, response.status);
    }

    const cached = readCachedStoreContext(expectedType);
    if (cached?.store?.id) {
        applyStoreContext(cached);
        return cached;
    }

    throw new Error(
        'Store not configured. Set REACT_APP_STORE_ID or REACT_APP_ORG_SLUG + REACT_APP_STORE_SLUG, open /s/{orgSlug}/{storeSlug}, or use {subdomain}.localhost.',
    );
}
